import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

const WizardContext = createContext(null);

export const CATEGORIES = [
  { id: 'about_you',     label: 'About you',       stepIds: ['about_you'] },
  { id: 'involvement',   label: 'Your involvement', stepIds: ['involvement'] },
  { id: 'opportunities', label: 'Opportunities',    stepIds: ['opportunities'] },
];

const STEPS = [
  { id: 'about_you',     next: () => 'involvement' },
  { id: 'involvement',   next: () => 'opportunities' },
  { id: 'opportunities', next: null },
];

const stepsMap = Object.fromEntries(STEPS.map(s => [s.id, s]));

WizardProvider.propTypes = {
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export function WizardProvider(props) {
  const { onClose, children } = props;
  const [history, setHistory] = useState([STEPS[0].id]);
  const [nextDisabled, setNextDisabled] = useState(false);
  const onBeforeNextRef = useRef(null);

  const activeStepId = history[history.length - 1];
  const activeStep = stepsMap[activeStepId];
  const completedStepIds = history.slice(0, -1);
  const isFirst = history.length === 1;
  const isLast = !activeStep?.next;

  const registerOnBeforeNext = useCallback((fn) => {
    onBeforeNextRef.current = fn;
  }, []);

  const goNext = useCallback(async () => {
    if (onBeforeNextRef.current) {
      const canProceed = await onBeforeNextRef.current();
      if (!canProceed) return;
    }
    if (isLast) {
      onClose?.();
      return;
    }
    const nextId = activeStep.next();
    setHistory(h => [...h, nextId]);
    onBeforeNextRef.current = null;
  }, [isLast, activeStep, onClose]);

  const goBack = useCallback(() => {
    onBeforeNextRef.current = null;
    setHistory(h => h.length > 1 ? h.slice(0, -1) : h);
  }, []);

  return (
    <WizardContext.Provider value={{
      activeStepId,
      completedStepIds,
      isFirst,
      isLast,
      goNext,
      goBack,
      nextDisabled,
      setNextDisabled,
      registerOnBeforeNext,
    }}>
      {children}
    </WizardContext.Provider>
  );
}

export function useWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used within <WizardProvider>');
  return ctx;
}
